import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as timezone from 'dayjs/plugin/timezone';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(utc.default);
dayjs.extend(timezone.default);
dayjs.extend(customParseFormat.default);

/** The date format used in API query strings and redirect URLs. */
export const API_DATE_FORMAT = 'YYYY-MM-DD+HH:mm:ss';

/** Normalize Etc/UTC to UTC — they are equivalent IANA timezones but UTC is cleaner for display and comparison. */
export const normalizeTz = (tz: string): string => (tz === 'Etc/UTC' ? 'UTC' : tz);

/**
 * Resolve the user's selected timezone from localStorage, falling back to the browser's timezone.
 * Always returns a normalized string (never an array or object).
 */
export function resolveUserTimezone(): string {
    const saved = window.localStorage.getItem('dbmar-timeZone');
    const raw = saved ? JSON.parse(saved) : Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tz = typeof raw === 'string' ? raw : (Array.isArray(raw) ? raw[0] : 'UTC');
    return normalizeTz(tz);
}

/**
 * Unwrap a query-string parameter that may be a string, string[], or null.
 * Returns the first value if it's an array, or the fallback if null/undefined.
 */
export function unwrapQueryParam(param: string | (string | null)[] | null | undefined, fallback: string): string {
    if (param == null) return fallback;
    if (typeof param === 'string') return param;
    if (Array.isArray(param)) return param[0] || fallback;
    return fallback;
}

/**
 * Normalize a time string from URL params.
 * Handles missing seconds (HH:mm → HH:mm:00) and space-separated dates (YYYY-MM-DD HH:mm → YYYY-MM-DD+HH:mm).
 */
export function normalizeTimeParam(s: string): string {
    const str = s.toString().trim().replace(/^(\d{4}-\d{2}-\d{2})\s(\d{2}:\d{2})$/, '$1+$2');
    return /^\d{4}-\d{2}-\d{2}\+\d{2}:\d{2}$/.test(str) ? `${str}:00` : str;
}

/**
 * Parse a time string from URL params into a dayjs object in the given timezone.
 * Returns null if the result is not a valid date.
 */
export function parseUrlTime(str: string, tz: string): dayjs.Dayjs | null {
    const normalized = normalizeTimeParam(str);
    const parsed = dayjs.tz(normalized, API_DATE_FORMAT, tz);
    return parsed.isValid() ? parsed : null;
}

/** Get "now" in the given timezone. */
export function nowInTimezone(tz: string): dayjs.Dayjs {
    return dayjs.tz(undefined, tz);
}

/** Format a Date for API requests — outputs the date in the given timezone so it matches the tz param. */
export function formatDateForApi(date: Date, timezone: string, format = API_DATE_FORMAT): string {
    return dayjs.tz(date, timezone).format(format);
}

/**
 * Convert a Date (stored instant) to a Date that react-datepicker will DISPLAY correctly.
 * The picker shows dates in local time, so we create a Date whose local representation
 * matches the target timezone (e.g. 9:00 UTC should display as 9:00 when user selected UTC).
 */
export function dateForPickerDisplay(date: Date | null, timezone: string): Date | null {
    if (!date) return null;
    const d = dayjs.tz(date, timezone);
    return new Date(d.year(), d.month(), d.date(), d.hour(), d.minute(), d.second());
}

/**
 * Convert a Date from react-datepicker (user's selection) to the correct instant.
 * The picker gives us local time (what user saw); we interpret it as the selected timezone.
 */
export function dateFromPickerSelection(pickerDate: Date | null, timezone: string): Date | null {
    if (!pickerDate) return null;
    const str = dayjs.default(pickerDate).format('YYYY-MM-DD HH:mm:ss');
    return dayjs.tz(str, 'YYYY-MM-DD HH:mm:ss', timezone).toDate();
}

/** Return default period dates (2h ago → 1h ago, 1h ago → now) in the given timezone. */
export function getDefaultPeriodDates(timezone: string) {
    const now = nowInTimezone(timezone);
    return {
        firstPeriodStart: now.subtract(2, 'hour').toDate(),
        firstPeriodEnd: now.subtract(1, 'hour').toDate(),
        secondPeriodStart: now.subtract(1, 'hour').toDate(),
        secondPeriodEnd: now.toDate(),
    };
}

/** Build the `from=...&to=...&tz=...&interval=...` query string for API calls. */
export function buildApiQuery(timeRange: { from: dayjs.Dayjs; to: dayjs.Dayjs; previousFrom: dayjs.Dayjs; timezone: string; interval: number }, previous = false, interval?: number): string {
    const tz = resolveUserTimezone();
    const format = (value: dayjs.Dayjs) => formatDateForApi(value.toDate(), tz);
    const from = previous ? format(timeRange.previousFrom) : format(timeRange.from);
    const to = previous ? format(timeRange.from) : format(timeRange.to);
    return `from=${from}&to=${to}&tz=${tz}&interval=${(interval ?? timeRange.interval) || 0}`;
}

/** Build the `fm=...&to=...&tz=...&interval=...` query string for in-app redirect URLs. */
export function buildRedirectQuery(timeRange: { from: dayjs.Dayjs; to: dayjs.Dayjs; timezone: string; interval: number }): string {
    const fm = timeRange.from.format(API_DATE_FORMAT);
    const to = timeRange.to.format(API_DATE_FORMAT);
    return `fm=${fm}&to=${to}&tz=${timeRange.timezone}&interval=${timeRange.interval}`;
}

/** Convert timeslice to UTC ms. Parses timeslice in user's selected timezone since API returns timeslices in requested tz. */
export const getConvertedTimeToUTC = (value: any) => {
    return dayjs.tz(value.timeslice, resolveUserTimezone()).valueOf();
};
